import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { Settings } from 'react-slick';

import type { Dispatch, RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { CrmWidgetConfigComponentType } from '@swf/generated/types/widgets/CrmWidget';

import Carousel from './components/Carousel/Carousel';
import CrmIframe from './components/CrmIframe/CrmIframe';
import NavCarousel from './components/NavCarousel/NavCarousel';

export const CrmZoneContext = React.createContext('');

interface Props {
  zoneId: string;
  contents?: string[];
  componentType: CrmWidgetConfigComponentType;
  settings?: Settings;
  scrolling?: string;
  dynamicHeight?: boolean;
  visible?: boolean;
}

const CrmZone: React.FC<Props> = ({
  zoneId,
  contents,
  settings,
  scrolling,
  dynamicHeight,
  componentType,
  visible
}) => {
  const { contents: fetchedContents } = useSelector((rootState: RootState) =>
    select.crm.getZone(rootState, zoneId)
  );
  const {
    crm: { fetchZone }
  } = useDispatch<Dispatch>();

  const zoneContents = fetchedContents || contents;

  const renderZone = useCallback(
    (slides: string[]) => {
      const zoneType = slides.length === 1 ? 'Single' : componentType;
      switch (zoneType) {
        case 'Carousel':
          return <Carousel slides={slides} settings={settings} />;
        case 'NavCarousel':
          return <NavCarousel slides={slides} settings={settings} />;
        default:
          return (
            <CrmIframe
              html={slides[0]}
              scrolling={scrolling}
              dynamicHeight={dynamicHeight}
              visible={visible}
              data-testid={'crm-zone'}
            />
          );
      }
    },
    [componentType, settings, scrolling, dynamicHeight, visible]
  );

  useEffect(() => {
    if (!zoneContents) {
      fetchZone({ zoneId });
    }
  }, [fetchZone, zoneContents, zoneId]);

  if (!zoneContents?.length || (zoneContents?.length === 1 && zoneContents[0] === '')) {
    return null;
  }

  return (
    <CrmZoneContext.Provider value={zoneId}>{renderZone(zoneContents)}</CrmZoneContext.Provider>
  );
};

export default CrmZone;
